import { Play } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent } from "../../ui/components/ui/dialog";
import { FeatureHintVideo } from "./useFeatureHints";

/** Wie ein Anbieter heisst - auch die Schreibmaske zeigt diese Namen. */
export const ANBIETER_NAME: Record<FeatureHintVideo["provider"], string> = {
  youtube: "YouTube",
  vimeo: "Vimeo",
  loom: "Loom",
};

/**
 * Das Video einer Neuigkeit (KAN-276, A12).
 *
 * Erst ein Knopf mit Vorschaubild, dann auf Klick der Rahmen des Anbieters in einem Fenster
 * ueber der Seite. Genau in dieser Reihenfolge, weil vor dem Klick keine Anfrage zu
 * YouTube, Vimeo oder Loom gehen darf (Verbotsliste 9) — auch das Vorschaubild kommt aus
 * unserem Ablageort.
 *
 * Dasselbe Bauteil in der Vollansicht und im Fenster beim Anmelden: Ein Video sieht an
 * beiden Stellen gleich aus und verhaelt sich gleich.
 */
export const VideoKarte = ({ video, onAbspielen }: { video: FeatureHintVideo; onAbspielen?: () => void }) => {
  const [offen, setOffen] = useState(false);
  const beschriftung = video.title || "Video abspielen";
  const herkunft = [ANBIETER_NAME[video.provider], video.duration].filter(Boolean).join(" · ");

  return (
    <>
      <button
        type="button"
        onClick={() => {
          onAbspielen?.();
          setOffen(true);
        }}
        className="flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white p-2.5 text-left hover:bg-gray-50"
      >
        <span className="relative flex h-[54px] w-24 shrink-0 items-center justify-center overflow-hidden rounded-md bg-gray-800">
          {video.thumbnailUrl && <img src={video.thumbnailUrl} alt="" className="h-full w-full object-cover object-center" />}
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90">
              <Play className="h-3.5 w-3.5 translate-x-[1px] fill-gray-900 text-gray-900" />
            </span>
          </span>
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-medium text-gray-900">Video abspielen</span>
          {video.title && <span className="block truncate text-[13px] leading-snug text-gray-600">{video.title}</span>}
          <span className="block text-xs text-gray-500">{herkunft}</span>
        </span>
      </button>

      <Dialog open={offen} onOpenChange={setOffen}>
        {/* Ueber dem Neuigkeiten-Bereich (z-[110]), nicht darunter: Ein Dialog steht hier
            sonst hinter dem Feld, aus dem er geoeffnet wurde, und wird rechts abgeschnitten.
            Die Abdunklung muss mit hoch, sonst bleibt der Bereich daneben hell. */}
        <DialogContent className="z-[121] max-w-3xl p-0" overlayClassName="z-[120]">
          <div className="flex items-start justify-between gap-4 px-4 pb-3 pt-4">
            <div className="min-w-0">
              <h2 className="truncate text-base font-bold text-gray-900">{beschriftung}</h2>
              <p className="mt-0.5 text-[13px] text-gray-500">{herkunft}</p>
            </div>
          </div>
          {/* Erst hier entsteht der Rahmen — vor diesem Klick kennt der Anbieter den Kunden nicht. */}
          <iframe
            src={video.embedUrl}
            title={beschriftung}
            allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            className="aspect-video w-full border-0 bg-black"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
